import React, { createContext, useContext, useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import { useLanguage } from './LanguageContext';

const EmailVerificationContext = createContext();

export const useEmailVerification = () => {
  const context = useContext(EmailVerificationContext);
  if (!context) {
    throw new Error('useEmailVerification must be used within an EmailVerificationProvider');
  }
  return context;
};

export const EmailVerificationProvider = ({ children }) => {
  const { t } = useLanguage();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null); // 'pending', 'verified', 'failed'
  const [verificationError, setVerificationError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Check if user's email is verified
  const checkVerificationStatus = async (userId) => {
    try {
      setIsVerifying(true);
      const response = await userAPI.checkEmailVerification(userId);
      setVerificationStatus(response.verified ? 'verified' : 'pending');
      setVerificationError(null);
      return response.verified;
    } catch (error) {
      console.error('Email verification check failed:', error);
      setVerificationStatus('failed');
      setVerificationError(error.message || t('verificationStatusCheckFailed'));
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  // Send verification email
  const sendVerificationEmail = async (email) => {
    try {
      setIsVerifying(true);
      setVerificationError(null);
      
      const response = await userAPI.sendVerificationEmail(email);
      
      if (response.success) {
        // Start cooldown timer (60 seconds)
        setResendCooldown(60);
        startCooldownTimer();
        return { success: true, message: t('verificationEmailSent') };
      } else {
        throw new Error(response.message || t('verificationEmailSendFailed'));
      }
    } catch (error) {
      console.error('Send verification email failed:', error);
      setVerificationError(error.message || t('verificationEmailSendFailed'));
      return { success: false, message: error.message || t('verificationEmailSendFailed') };
    } finally {
      setIsVerifying(false);
    }
  };

  // Verify email with token
  const verifyEmail = async (token) => {
    try {
      setIsVerifying(true);
      setVerificationError(null);
      
      const response = await userAPI.verifyEmail(token);
      
      if (response.success) {
        setVerificationStatus('verified');
        return { success: true, message: t('emailVerifiedSuccessfully') };
      } else {
        throw new Error(response.message || t('emailVerificationFailed'));
      }
    } catch (error) {
      console.error('Email verification failed:', error);
      setVerificationStatus('failed');
      setVerificationError(error.message || t('emailVerificationFailed'));
      return { success: false, message: error.message || t('emailVerificationFailed') };
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend verification email
  const resendVerificationEmail = async (email) => {
    if (resendCooldown > 0) {
      return {
        success: false,
        message: t('pleaseWaitSeconds').replace('{seconds}', resendCooldown)
      };
    }
    
    return await sendVerificationEmail(email);
  };

  // Start cooldown timer
  const startCooldownTimer = () => {
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Clear verification status
  const clearVerificationStatus = () => {
    setVerificationStatus(null);
    setVerificationError(null);
    setResendCooldown(0);
  };

  const value = {
    isVerifying,
    verificationStatus,
    verificationError,
    resendCooldown,
    checkVerificationStatus,
    sendVerificationEmail,
    verifyEmail,
    resendVerificationEmail,
    clearVerificationStatus
  };

  return (
    <EmailVerificationContext.Provider value={value}>
      {children}
    </EmailVerificationContext.Provider>
  );
};

export default EmailVerificationContext;
